import React, { useState } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import css from './AnnouncementBanner.module.css';

const ANNOUNCEMENT_KEY = 'ith_announcement_dismissed';

/**
 * AnnouncementBanner — dismissible site-wide announcement bar.
 *
 * Configured via ENV variables (baked in at build time):
 *   REACT_APP_ANNOUNCEMENT_TEXT    — banner text (required to show)
 *   REACT_APP_ANNOUNCEMENT_LINK    — optional CTA link URL
 *   REACT_APP_ANNOUNCEMENT_LINK_TEXT — optional CTA link text (default: "Learn more")
 *   REACT_APP_ANNOUNCEMENT_TYPE    — 'info' | 'warning' | 'success' (default: 'info')
 *   REACT_APP_ANNOUNCEMENT_ID      — unique ID to reset dismissal state across deploys
 *
 * Usage — add to TopbarContainer or app.js:
 *   <AnnouncementBanner />
 *
 * Dismissal is stored in localStorage keyed by announcement ID.
 * Changing REACT_APP_ANNOUNCEMENT_ID resets dismissal.
 */
const AnnouncementBanner = ({ className }) => {
  const text = process.env.REACT_APP_ANNOUNCEMENT_TEXT;
  const link = process.env.REACT_APP_ANNOUNCEMENT_LINK;
  const linkText = process.env.REACT_APP_ANNOUNCEMENT_LINK_TEXT || 'Learn more';
  const type = process.env.REACT_APP_ANNOUNCEMENT_TYPE || 'info';
  const announcementId = process.env.REACT_APP_ANNOUNCEMENT_ID || 'v1';

  const storageKey = `${ANNOUNCEMENT_KEY}_${announcementId}`;

  const [dismissed, setDismissed] = useState(() => {
    try {
      return typeof window !== 'undefined' && localStorage.getItem(storageKey) === 'true';
    } catch (_) {
      return false;
    }
  });

  if (!text || dismissed) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(storageKey, 'true');
    } catch (_) {}
    setDismissed(true);
  };

  return (
    <div className={classNames(css.root, css[`type_${type}`], className)} role="alert">
      <span className={css.text}>{text}</span>
      {link && (
        <a href={link} className={css.link} target="_blank" rel="noopener noreferrer">
          {linkText}
        </a>
      )}
      <button
        type="button"
        onClick={handleDismiss}
        className={css.closeBtn}
        aria-label="Dismiss announcement"
      >
        ×
      </button>
    </div>
  );
};

AnnouncementBanner.defaultProps = { className: null };
AnnouncementBanner.propTypes = { className: string };

export default AnnouncementBanner;
